import React from 'react';
import { Heart, RotateCcw, Trash2, X } from 'lucide-react';
import { formatProductLabel, formatIngredientDisplay } from '../utils/formatDisplayLabel';

const statusClass = (s = '') => {
  const t = s.toLowerCase();
  if (t.includes('not')) return 'bg-red-100 text-red-700';
  if (t.includes('maybe') || t.includes('unknown')) return 'bg-amber-100 text-amber-800';
  if (t.includes('vegan') || t.includes('plant')) return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
};

const statusLabel = (s = '') => {
  const t = s.toLowerCase();
  if (t.includes('not')) return 'Not Vegan';
  if (t.includes('maybe') || t.includes('unknown')) return 'Maybe';
  if (t.includes('vegan') || t.includes('plant')) return 'Vegan';
  return 'Unknown';
};

export function FavoritesModal({
  open,
  items,
  onDismiss,
  onRerun,
  onRemove,
  onClear,
  storageDescription = 'Favorites are saved in this browser on this device.',
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="favorites-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onDismiss();
      }}
    >
      <div
        className="relative max-h-[min(90vh,720px)] w-full max-w-2xl overflow-y-auto rounded-2xl border border-emerald-100 bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          aria-label="Close favorites"
        >
          <X className="h-5 w-5" />
        </button>

        <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Saved</p>
        <h2 id="favorites-title" className="mt-1 pr-10 text-xl font-black text-gray-900">
          Favorites
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-600">
          {storageDescription}
        </p>

        <div className="mt-5 space-y-3">
          {items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-6 text-center">
              <Heart className="mx-auto h-8 w-8 text-emerald-600" />
              <p className="mt-3 text-sm font-bold text-slate-900">No favorites yet</p>
              <p className="mt-1 text-sm text-slate-600">Save a result after checking a product or ingredient list.</p>
            </div>
          ) : items.map((item) => (
            <article
              key={item.id}
              className="rounded-2xl border border-emerald-100 bg-white p-4 shadow-sm shadow-emerald-950/5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-emerald-600">
                    {item.mode}
                  </p>
                  <h3 className="mt-1 break-words text-base font-black text-slate-900">
                    {formatProductLabel(item.favoriteLabel || item.productMatch || item.input || 'Saved result')}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusClass(item.status)}`}>
                      {statusLabel(item.status)}
                    </span>
                    {item.confidence != null && (
                      <span className="text-xs font-semibold text-slate-500">
                        {Math.round(item.confidence > 1 ? item.confidence : item.confidence * 100)}% confidence
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {(item.nonPlantBasedIngredients?.length > 0 || item.questionableIngredients?.length > 0) && (
                <div className="mt-3 space-y-1 border-t border-emerald-100 pt-3 text-xs text-slate-600">
                  {item.nonPlantBasedIngredients?.length > 0 && (
                    <p>
                      <span className="font-bold text-slate-500">Not vegan:</span>{' '}
                      {item.nonPlantBasedIngredients.map((x) => formatIngredientDisplay(String(x))).join(', ')}
                    </p>
                  )}
                  {item.questionableIngredients?.length > 0 && (
                    <p>
                      <span className="font-bold text-slate-500">Questionable:</span>{' '}
                      {item.questionableIngredients.map((x) => formatIngredientDisplay(String(x))).join(', ')}
                    </p>
                  )}
                </div>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onDismiss();
                    onRerun(item);
                  }}
                  className="inline-flex items-center gap-1 rounded-xl border border-indigo-200 bg-white px-3 py-1.5 text-xs font-bold text-indigo-600 transition-colors hover:bg-indigo-50"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Rescan
                </button>
                <button
                  type="button"
                  onClick={() => onRemove(item.id)}
                  className="inline-flex items-center gap-1 rounded-xl border border-orange-200 bg-white px-3 py-1.5 text-xs font-bold text-orange-600 transition-colors hover:bg-orange-50"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            </article>
          ))}
        </div>

        {items.length > 0 && (
          <button
            type="button"
            onClick={onClear}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-red-200 bg-white py-2 text-sm font-semibold text-red-500 transition-colors hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" />
            Clear favorites
          </button>
        )}
      </div>
    </div>
  );
}
